/**
 * ActivityDrawer — slides in from the right, lists recent reward
 * activity rows. Each row clickable for an inspect detail panel.
 *
 * Reduce-motion: the slide is replaced by a fade. Driven by
 * `prefers-reduced-motion` via CSS.
 */
import { useMemo, useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import type { ActivityRow } from '../../lib/api';

interface Props {
  open: boolean;
  items: ActivityRow[];
  onClose: () => void;
}

const CLEARED_KEY = 'tt:activity:clearedBefore';
const DISMISSED_KEY = 'tt:activity:dismissed'; // JSON array of "ts|title"

function readClearedBefore(): number {
  try {
    const v = Number(localStorage.getItem(CLEARED_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}
function writeClearedBefore(ts: number): void {
  try { localStorage.setItem(CLEARED_KEY, String(ts)); } catch { /* */ }
}
function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const list = JSON.parse(raw) as string[];
    return new Set(Array.isArray(list) ? list : []);
  } catch { return new Set(); }
}
function writeDismissed(s: Set<string>): void {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(s).slice(-200))); } catch { /* */ }
}
function rowKey(it: ActivityRow): string {
  return `${it.ts}|${it.title}`;
}

export function ActivityDrawer({ open, items, onClose }: Props) {
  const [inspect, setInspect] = useState<ActivityRow | null>(null);
  const [clearedBefore, setClearedBefore] = useState<number>(readClearedBefore);
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);

  const visible = useMemo(() => items.filter((it) => {
    if (it.ts <= clearedBefore) return false;
    if (dismissed.has(rowKey(it))) return false;
    return true;
  }), [items, clearedBefore, dismissed]);

  function dismissRow(it: ActivityRow) {
    const next = new Set(dismissed);
    next.add(rowKey(it));
    setDismissed(next);
    writeDismissed(next);
  }
  function clearAll() {
    // BUG: items[0] was assumed to be the newest, but the API can
    // return any order. Compute max ts across all items to ensure
    // every existing notification is below the cleared-before line.
    const newest = items.reduce((m, it) => Math.max(m, it.ts), 0) || Date.now();
    setClearedBefore(newest);
    writeClearedBefore(newest);
    // Also persist all current item keys in dismissed so even items
    // with identical timestamps don't sneak back in.
    const next = new Set(dismissed);
    for (const it of items) next.add(rowKey(it));
    setDismissed(next);
    writeDismissed(next);
  }

  if (!open) return null;
  return (
    <div className="activity-drawer-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <aside className="activity-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="activity-drawer-head">
          <h2 className="display">Recent rewards</h2>
          <div style={{ display: 'inline-flex', gap: '0.6rem', alignItems: 'center' }}>
            {visible.length > 0 && (
              <button type="button" className="link icon-btn" onClick={clearAll} title="Clear all rewards">
                <Trash2 size={14} />Clear all
              </button>
            )}
            <button type="button" className="link" onClick={onClose}>close</button>
          </div>
        </header>
        {visible.length === 0 ? (
          <p className="muted activity-drawer-empty">
            Nothing yet. Play a round and your XP, level-ups, achievements, and
            cosmetic unlocks will land here. Rows live 30 days.
          </p>
        ) : (
          <ul className="activity-drawer-list">
            {visible.map((it, i) => (
              <li key={`act-${it.ts}-${i}`} className="activity-drawer-li">
                <button
                  type="button"
                  className={`activity-drawer-row activity-rarity-${it.rarity}`}
                  onClick={() => setInspect(it)}
                >
                  <span className="activity-drawer-icon" aria-hidden="true">{it.icon ?? '✦'}</span>
                  <div className="activity-drawer-text">
                    <strong>{it.title}</strong>
                    <small>{it.subtitle ?? ''}</small>
                  </div>
                  <div className="activity-drawer-meta">
                    {it.rewardChip && <span className="activity-drawer-chip">{it.rewardChip}</span>}
                    <small>{formatRelative(it.ts)}</small>
                  </div>
                </button>
                <button
                  type="button"
                  className="activity-drawer-dismiss"
                  onClick={(e) => { e.stopPropagation(); dismissRow(it); }}
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {inspect && (
          <div className="activity-drawer-detail" aria-live="polite">
            <header>
              <span>{kindLabel(inspect.kind)}</span>
              <button type="button" className="link" onClick={() => setInspect(null)}>close</button>
            </header>
            <strong>{inspect.title}</strong>
            <p>{inspect.subtitle ?? bodyForKind(inspect.kind)}</p>
            <small>{inspect.rewardChip ? `Reward: ${inspect.rewardChip}.` : ''} Earned {formatAbsolute(inspect.ts)}.</small>
          </div>
        )}
      </aside>
    </div>
  );
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'level': return 'Level up';
    case 'achievement': return 'Achievement';
    case 'cosmetic': return 'Cosmetic unlock';
    case 'currency': return 'XP earned';
    case 'mastery': return 'Mastery';
    case 'season': return 'Season';
    default: return 'Reward';
  }
}
function bodyForKind(kind: string): string {
  switch (kind) {
    case 'level': return 'Your account level went up. Higher levels grow your profile and unlock cosmetic milestones.';
    case 'achievement': return 'A milestone you tripped during play. Achievements live in your profile and may have rewards attached.';
    case 'cosmetic': return 'A cosmetic that just crossed its unlock threshold. Equip it from the customize editor.';
    case 'currency': return 'Account XP earned this round, itemized by participation, place, and bonuses.';
    case 'mastery': return 'You crossed a mastery tier in this game.';
    default: return 'Reward earned during a round.';
  }
}
function formatRelative(ts: number): string {
  const dMs = Date.now() - ts;
  const sec = Math.max(0, Math.floor(dMs / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function formatAbsolute(ts: number): string {
  return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
